import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  HStack,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { getBotTicket, getBotTicketSuggestions, getBotTickets } from '../store/api_funcs';
import { useStores } from '../store/store_context';

const statusText = {
  new: 'Новая',
  in_work: 'В работе',
  done: 'Обработана',
};

const kindText = {
  offer: 'Заявка',
  support: 'Поддержка',
  manager: 'Менеджер',
};

const fmt = value => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('ru-RU');
};

const BotTicketsPage = () => {
  const { pageStore } = useStores();
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('all');
  const [kind, setKind] = useState('all');
  const [hours, setHours] = useState(24);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getBotTickets({ q, status, kind, hours }, pageStore.token);
      const list = data.items || [];
      setItems(list);
      if (!selected && list.length) setSelected(list[0]);
      if (selected && !list.some(item => item.ticket_id === selected.ticket_id)) {
        setSelected(list[0] || null);
      }
    } catch (error) {
      toast({ title: 'Заявки не загружены', description: error.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  const loadSuggestions = async value => {
    if (!value) {
      setSuggestions([]);
      return;
    }
    const data = await getBotTicketSuggestions(value, pageStore.token).catch(() => ({ items: [] }));
    setSuggestions(data.items || []);
  };

  const openTicket = async ticketId => {
    const data = await getBotTicket(ticketId, pageStore.token);
    setSelected(data);
  };

  useEffect(() => {
    load();
  }, [status, kind, hours]);

  useEffect(() => {
    const timer = setTimeout(() => loadSuggestions(q), 250);
    return () => clearTimeout(timer);
  }, [q]);

  const stats = useMemo(() => ({
    all: items.length,
    new: items.filter(item => item.status === 'new').length,
    inWork: items.filter(item => item.status === 'in_work').length,
    done: items.filter(item => item.status === 'done').length,
  }), [items]);

  return (
    <VStack align="stretch" spacing={5}>
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
        <Box bg="rgba(255,255,255,0.045)" border="1px solid rgba(255,255,255,0.12)" borderRadius="8px" p={4}>
          <Text color="rgba(255,255,255,0.55)">Всего</Text>
          <Text fontSize="28px" fontWeight="700">{stats.all}</Text>
        </Box>
        <Box bg="rgba(255,255,255,0.045)" border="1px solid rgba(255,255,255,0.12)" borderRadius="8px" p={4}>
          <Text color="rgba(255,255,255,0.55)">Новые</Text>
          <Text fontSize="28px" fontWeight="700">{stats.new}</Text>
        </Box>
        <Box bg="rgba(255,255,255,0.045)" border="1px solid rgba(255,255,255,0.12)" borderRadius="8px" p={4}>
          <Text color="rgba(255,255,255,0.55)">В работе</Text>
          <Text fontSize="28px" fontWeight="700">{stats.inWork}</Text>
        </Box>
        <Box bg="rgba(255,255,255,0.045)" border="1px solid rgba(255,255,255,0.12)" borderRadius="8px" p={4}>
          <Text color="rgba(255,255,255,0.55)">Обработаны</Text>
          <Text fontSize="28px" fontWeight="700">{stats.done}</Text>
        </Box>
      </SimpleGrid>

      <HStack align="start" spacing={4}>
        <VStack align="stretch" flex="1" spacing={3}>
          <HStack>
            <Box position="relative" flex="1">
              <Input value={q} onChange={event => setQ(event.target.value)} placeholder="Поиск по заявке, панели, адресу, телефону" />
              {suggestions.length ? (
                <Box position="absolute" zIndex="5" top="44px" left="0" right="0" bg="#171827" border="1px solid rgba(255,255,255,0.16)" borderRadius="8px" overflow="hidden">
                  {suggestions.map(item => (
                    <Box key={item.ticket_id} p={3} cursor="pointer" _hover={{ bg: 'rgba(139,92,246,0.22)' }} onClick={() => { setQ(item.ticket_id); setSuggestions([]); openTicket(item.ticket_id); }}>
                      <Text fontWeight="700">{item.ticket_id} · {item.panel_address || item.panel_fabric || item.username || '—'}</Text>
                      <Text fontSize="13px" color="rgba(255,255,255,0.55)">{fmt(item.created_at)}</Text>
                    </Box>
                  ))}
                </Box>
              ) : null}
            </Box>
            <Select value={status} onChange={event => setStatus(event.target.value)} maxW="190px">
              <option value="all">Все статусы</option>
              <option value="new">Новые</option>
              <option value="in_work">В работе</option>
              <option value="done">Обработаны</option>
            </Select>
            <Select value={kind} onChange={event => setKind(event.target.value)} maxW="180px">
              <option value="all">Все типы</option>
              <option value="offer">Заявки</option>
              <option value="support">Поддержка</option>
              <option value="manager">Менеджер</option>
            </Select>
            <Select value={hours} onChange={event => setHours(Number(event.target.value))} maxW="160px">
              <option value={24}>24 часа</option>
              <option value={72}>3 дня</option>
              <option value={168}>7 дней</option>
              <option value={720}>30 дней</option>
            </Select>
            <Button onClick={load} bg="#8B5CF6" color="white" _hover={{ bg: '#7C3AED' }}>{loading ? <Spinner size="sm" /> : 'Обновить'}</Button>
          </HStack>
          <TableContainer border="1px solid rgba(255,255,255,0.14)" borderRadius="8px" bg="rgba(255,255,255,0.025)">
            <Table variant="unstyled">
              <Thead>
                <Tr borderBottom="1px solid rgba(255,255,255,0.12)">
                  <Th>Заявка</Th>
                  <Th>Статус</Th>
                  <Th>Панель</Th>
                  <Th>Адрес</Th>
                  <Th>Телефон</Th>
                  <Th>Создана</Th>
                </Tr>
              </Thead>
              <Tbody>
                {items.map(item => (
                  <Tr key={item.ticket_id} cursor="pointer" bg={selected?.ticket_id === item.ticket_id ? 'rgba(139,92,246,0.24)' : 'transparent'} borderBottom="1px solid rgba(255,255,255,0.08)" _hover={{ bg: 'rgba(139,92,246,0.16)' }} onClick={() => setSelected(item)}>
                    <Td>{item.ticket_id}<Text fontSize="12px" color="rgba(255,255,255,0.52)">{kindText[item.kind]}</Text></Td>
                    <Td><Badge colorScheme={item.status === 'done' ? 'green' : item.status === 'in_work' ? 'purple' : 'red'}>{statusText[item.status] || item.status}</Badge></Td>
                    <Td>{item.panel_fabric || '—'}</Td>
                    <Td>{item.panel_address || item.panel_comment || '—'}</Td>
                    <Td>{item.phone || '—'}</Td>
                    <Td>{fmt(item.created_at)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </VStack>

        <Box w="390px" border="1px solid rgba(255,255,255,0.16)" borderRadius="8px" bg="rgba(255,255,255,0.035)" p={5} position="sticky" top="20px">
          {selected ? (
            <VStack align="stretch" spacing={3}>
              <Text fontSize="24px" fontWeight="800">{selected.ticket_id}</Text>
              <Badge alignSelf="flex-start" colorScheme={selected.status === 'done' ? 'green' : selected.status === 'in_work' ? 'purple' : 'red'}>{statusText[selected.status]}</Badge>
              <Text>Тип: {kindText[selected.kind] || selected.kind}</Text>
              <Text>Панель: {selected.panel_fabric || '—'}</Text>
              <Text>Адрес: {selected.panel_address || '—'}</Text>
              <Text>Комментарий: {selected.panel_comment || '—'}</Text>
              <Text>Разрешение: {selected.panel_dimensions || '—'}</Text>
              <Text>Пользователь: @{selected.username || 'без username'}</Text>
              <Text>Телефон: {selected.phone || '—'}</Text>
              <Text>Тип/проблема: {selected.object_type || '—'}</Text>
              <Text>Интерес/объект: {selected.product_interest || '—'}</Text>
              <Text>Описание: {selected.message_text || '—'}</Text>
              <Text>Создана: {fmt(selected.created_at)}</Text>
              <Text>Взял: {selected.taken_by || '—'}</Text>
              <Text>Обработал: {selected.processed_by || '—'}</Text>
            </VStack>
          ) : (
            <Text color="rgba(255,255,255,0.55)">Выберите заявку</Text>
          )}
        </Box>
      </HStack>
    </VStack>
  );
};

export default BotTicketsPage;
